// Simples Nacional calculado no sistema (backend: /api/v1/simples-nacional).
import { api } from '../../../../services/api';

const BASE = '/api/v1/simples-nacional';

export const TELA_PATH = '/modulesHome/supervisor/simples';
export const TELA_FATURAS_PATH = '/modulesHome/supervisor/simples/faturas';

export type Situacao =
  | 'nao_calculada'
  | 'calculada'
  | 'aprovada'
  | 'transmitindo'
  | 'transmitida'
  | 'concluida'
  | 'erro';

export interface IEmpresaSn {
  id: number;
  nome: string;
  razao_social?: string | null;
  cnpj?: string | null;
  external_id?: string | null;
  cidade?: string | null;
  uf?: string | null;
  fiscal?: string | null;
  nivel?: string | null;
}

export interface IAtividadeSn {
  item: string;
  anexo: 3 | 4 | 5;
  fator_r: boolean;
  nota?: string | null;
}

export interface IParametroSn {
  iss_fixo: boolean;
  valor_fixo_iss: number | null;
  icms_fora_do_das: boolean;
  anexo_padrao_servico: 3 | 4 | 5 | null;
  inicio_atividade: string | null;
  atividades: IAtividadeSn[];
  filiais: string[];
  observacao: string | null;
  /** Orquestrador › Agendamentos: manual | só calcular | calcular e transmitir */
  automacao?: 'manual' | 'calcular' | 'transmitir';
  atualizado_por_nome?: string | null;
  dt_atualizacao?: string | null;
}

export interface IApuracaoSn {
  id: number;
  competencia: string;
  situacao: Situacao;
  receita_bruta: number | null;
  receita_externa: number | null;
  valor_das: number | null;
  valor_dominio: number | null;
  valor_serpro: number | null;
  valor_mes_anterior: number | null;
  rbt12: number | null;
  rbt12_fonte: string | null;
  rbt12_proprio: number | null;
  rbt12_receita: number | null;
  fator_r: number | null;
  folha12: number | null;
  por_tributo: Record<string, number>;
  pendencias_abertas: number;
  bloqueios_abertos: number;
  calculado_em: string | null;
  aprovado_por_nome: string | null;
  aprovado_em: string | null;
  transmitido_em: string | null;
  numero_declaracao: string | null;
  /** declaração que já estava no PGDAS-D sem ter sido transmitida por aqui */
  declarada_por_fora?: boolean;
  declaracao_receita?: {
    numero: string;
    importada_em: string;
    rbt12: number | null;
    meses_lidos: number;
  } | null;
  numero_das: string | null;
  das_vencimento: string | null;
  das_total: number | null;
  erro: string | null;
  /** download em lote (ZIP): quando, quem e quantas vezes */
  docs_baixados_em?: string | null;
  docs_baixados_por_nome?: string | null;
  docs_baixados_vezes?: number;
}

/** Apuração do Domínio por anexo (extract_simples_nacional): só gabarito. */
export interface IDominioAnexo {
  anexo: number;
  receita: number | null;
  valor: number | null;
  aliquota_efetiva: number | null;
  tributos: Record<string, number | null>;
  iss_retido: number | null;
}

export interface IPendenciaResumo {
  id: number;
  codigo: string;
  nivel: 'info' | 'atencao' | 'bloqueio';
  mensagem: string;
  documento: string | null;
}

export interface IItemLista {
  empresa: IEmpresaSn;
  apuracao: IApuracaoSn | null;
  situacao: Situacao;
  parametro: IParametroSn | null;
  parcelas: IParcela[];
  dominio: IDominioAnexo[];
  pendencias: IPendenciaResumo[];
}

export interface ILista {
  items: IItemLista[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/** Etapa do mês: cada empresa está em uma só (somam o total da carteira). */
export type Etapa =
  | 'nao_calculada'
  | 'bloqueio'
  | 'atencao'
  | 'pronta_aprovar'
  | 'aprovada'
  | 'transmitida'
  | 'concluida'
  | 'erro';

export interface ITotais {
  total_empresas: number;
  nao_calculadas: number;
  por_situacao: Partial<Record<Situacao, number>>;
  etapas?: Record<Etapa, number>;
  valor_das: number;
  receita: number;
  valor_dominio?: number;
  /** DAS por etapa: aprovadas (a transmitir), transmitidas/concluídas e o resto */
  das?: { a_transmitir: number; declarado: number; em_conferencia: number };
  com_pendencias: number;
  com_bloqueio?: number;
  divergentes_dominio: number;
  batem_dominio?: number;
  sem_dominio?: number;
  sem_procuracao?: number;
  sem_movimento?: number;
  /** apurações sem fonte de RBT12 */
  sem_rbt12?: number;
  /** fatura nos 3 meses anteriores (Domínio espécie 42 ou tela de Faturas) ou no mês */
  com_fatura?: number;
  /** costuma ter fatura e nada lançado no mês */
  fatura_pendente?: number;
  /** transmitidas sem DAS/extrato gerado */
  aguardando_documentos?: number;
  /** concluídas ainda não baixadas em ZIP */
  aguardando_baixa?: number;
  /** já baixadas em ZIP ao menos uma vez */
  docs_baixados?: number;
  declaradas?: number;
  pct_declarado?: number;
}

/** Painel da carteira (aba principal): ver backend services/simples_nacional/painel.py */
export interface IPainel {
  competencia: string;
  total: number;
  /** média do progresso das empresas, 0 a 100 */
  progresso: number;
  etapas: Record<Etapa, number>;
  faixas: { progresso: number; empresas: number; pct: number }[];
  responsaveis: {
    responsavel: string;
    total: number;
    progresso: number;
    concluidas: number;
    travadas: number;
    etapas: Record<Etapa, number>;
  }[];
  travas: {
    codigo: string;
    nivel: 'atencao' | 'bloqueio';
    empresas: number;
    pct: number;
    exemplo: string;
  }[];
  travadas: {
    empresa_id: number;
    nome: string;
    external_id: string | null;
    fiscal: string | null;
    etapa: Etapa;
    bloqueios: number;
    pendencias: number;
    motivo: string | null;
  }[];
  evolucao: {
    competencia: string;
    total: number;
    progresso: number;
    concluidas: number;
    declaradas: number;
    travadas: number;
  }[];
}

export interface IResumoFaturas {
  empresas: number;
  por_tipo: Partial<Record<IFatura['tipo'], { quantidade: number; valor: number }>>;
  iss_retido: number;
  exterior: number;
}

export interface IListaFaturas {
  items: IFatura[];
  total: number;
  valor_total: number;
  resumo?: IResumoFaturas;
}

export interface IConfigSn {
  serpro_configurado: boolean;
  ambiente: string | null;
  certificado: { titular: string; valido_ate: string } | null;
  pendencia: string | null;
  /** Job noturno (03:30) recalcula as apurações sozinho. */
  recalculo_automatico: boolean;
  /** Orquestrador › Agendamentos do Simples */
  agendamento?: { ligado: boolean; ativas: string[] };
}

export interface IPendencia {
  id: number;
  codigo: string;
  nivel: 'info' | 'atencao' | 'bloqueio';
  mensagem: string;
  documento: string | null;
  situacao: 'aberto' | 'aceito' | 'ressalva' | 'resolvido';
  justificativa: string | null;
  resolvido_por_nome: string | null;
  resolvido_em: string | null;
}

export interface IArquivo {
  id: number;
  tipo: string;
  numero: string | null;
  dt_criacao: string;
}

export interface ICorrecao {
  id?: number;
  documento: string;
  ignorar: boolean;
  tipo: 'revenda' | 'industrializacao' | null;
  icms_st: boolean | null;
  monofasico: boolean | null;
  anexo: 3 | 4 | 5 | null;
  fator_r: boolean | null;
  iss_retido: boolean | null;
  motivo: string;
  usuario_nome?: string | null;
  dt_atualizacao?: string | null;
}

export interface IParcela {
  anexo: number;
  receita: number;
  fora_do_das: string[];
  faixa: number;
  aliquota_nominal: number;
  parcela_deduzir: number;
  aliquota_efetiva: number;
  valor: number;
  tributos: Record<string, number>;
}

export interface ILinha {
  documento: string;
  id_atividade: number;
  anexo: number;
  valor: number;
  fora_do_das: string[];
  qualificacoes: [string, string][];
  outro_municipio: string | null;
  isencoes: string[];
  estabelecimento: string | null;
}

export interface IDetalhe {
  parcelas?: IParcela[];
  linhas?: ILinha[];
  devolucoes?: number;
  importacao?: Record<string, number>;
  rbt12?: { criterio: string | null; serie: [string, number][] };
  fator_r?: { folha12: number; valor: number; atinge_28: boolean };
  recalculo?: { em: string; valor_das: number; receita: number };
  [k: string]: unknown;
}

export interface IDetalheApuracao {
  empresa: IEmpresaSn;
  parametro: IParametroSn | null;
  apuracao: IApuracaoSn | null;
  detalhe: IDetalhe | null;
  dominio: IDominioAnexo[];
  payload: unknown;
  resposta_serpro: unknown;
  pendencias: IPendencia[];
  arquivos: IArquivo[];
  correcoes: ICorrecao[];
  pode_aprovar: boolean;
}

export type AcaoPendencia = 'aceitar' | 'ressalva' | 'reabrir';

/** Bloqueios que o supervisor pode liberar com ressalva (backend: BLOQUEIOS_COM_RESSALVA). */
export const BLOQUEIOS_COM_RESSALVA = new Set(['rbt12_acima_limite']);

export type AcaoLote =
  | 'recalcular'
  | 'aprovar'
  | 'rascunho'
  | 'importar'
  | 'buscar_rbt12'
  | 'transmitir'
  | 'documentos';

/** Status da task (processing | completed | failed) + progresso do Redis (números em texto). */
export interface IStatusLote {
  status?: string;
  error?: string | null;
  progresso?: {
    acao?: string;
    total?: string | number;
    feitas?: string | number;
    ok?: string | number;
    resultados?: { empresa_id: number; ok: boolean; erro?: string; situacao?: string }[];
    /** empresas do lote, na ordem: as que não estão em `resultados` não rodaram */
    ids?: number[];
  } | null;
}

export interface IFatura {
  id?: number;
  empresa_id: number;
  competencia: string;
  data: string;
  tipo: 'servico' | 'revenda' | 'industrializacao' | 'locacao';
  descricao: string;
  numero_documento: string | null;
  tomador_doc: string | null;
  tomador_nome: string | null;
  valor: number;
  item_lc116: string | null;
  iss_retido: boolean;
  municipio_ibge: string | null;
  icms_st: boolean;
  monofasico: boolean;
  exterior: boolean;
  observacao: string | null;
  usuario_nome?: string | null;
  dt_atualizacao?: string | null;
  empresa?: IEmpresaSn;
  /** situação da apuração do mês da fatura */
  situacao_apuracao?: Situacao;
  /** mês transmitido: a fatura não muda (só por retificação) */
  travada?: boolean;
}

export interface IValorMensal {
  id?: number;
  competencia: string;
  fonte?: string;
  valor_interno?: number | null;
  valor_externo?: number | null;
  valor?: number | null;
  observacao?: string | null;
}

/** Mensagem legível do erro da API (422 do fluxo vem como {codigo, message}). */
export function mensagemErro(e: any, padrao = 'Não foi possível concluir.'): string {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message ?? padrao;
}

export type TipoDocumento = 'das' | 'extrato' | 'declaracao' | 'recibo';

export const TIPOS_DOCUMENTO: { tipo: TipoDocumento; rotulo: string; padrao: boolean }[] = [
  { tipo: 'das', rotulo: 'Guia (DAS)', padrao: true },
  { tipo: 'extrato', rotulo: 'Extrato (relatório)', padrao: true },
  { tipo: 'declaracao', rotulo: 'Declaração', padrao: false },
  { tipo: 'recibo', rotulo: 'Recibo', padrao: false },
];

const dados = <T>(r: { data: { data: T } }) => r.data.data;

export const snApi = {
  config: () => api.get(`${BASE}/config`).then(dados<IConfigSn>),
  listar: (params: Record<string, string | number | undefined>) =>
    api.get(`${BASE}/apuracoes`, { params }).then(dados<ILista>),
  painel: (competencia: string) =>
    api.get(`${BASE}/painel`, { params: { competencia } }).then(dados<IPainel>),
  totais: (competencia: string) =>
    api.get(`${BASE}/totais`, { params: { competencia } }).then(dados<ITotais>),
  detalhe: (empresaId: number, competencia: string) =>
    api.get(`${BASE}/apuracoes/${empresaId}/${competencia}`).then(dados<IDetalheApuracao>),
  calcular: (empresaId: number, competencia: string) =>
    api.post(`${BASE}/apuracoes/${empresaId}/${competencia}/calcular`).then(dados<IApuracaoSn>),
  aprovar: (id: number) => api.post(`${BASE}/apuracoes/${id}/aprovar`).then(dados<IApuracaoSn>),
  desaprovar: (id: number) =>
    api.post(`${BASE}/apuracoes/${id}/desaprovar`).then(dados<IApuracaoSn>),
  /** ressalva: libera bloqueio que admite (ex.: RBT12 acima do limite); justificativa obrigatória */
  pendencia: (id: number, acao: AcaoPendencia, justificativa?: string) =>
    api.patch(`${BASE}/pendencias/${id}`, { acao, justificativa }).then(dados),
  /** aceita as pendências de ATENÇÃO abertas (vazio = todas as empresas); bloqueio fica de fora */
  aceitarAtencoes: (competencia: string, empresaIds: number[], justificativa?: string) =>
    api
      .post(`${BASE}/pendencias/aceitar-atencoes`, {
        competencia,
        empresa_ids: empresaIds,
        justificativa: justificativa?.trim() || null,
      })
      .then(dados<{ aceitas: number; apuracoes: number }>),
  salvarCorrecao: (body: ICorrecao & { empresa_id: number; competencia: string }) =>
    api.put(`${BASE}/correcoes`, body).then(dados<IApuracaoSn>),
  excluirCorrecao: (id: number) => api.delete(`${BASE}/correcoes/${id}`).then(dados),
  lote: (acao: AcaoLote, competencia: string, empresaIds: number[] = []) =>
    api
      .post(`${BASE}/lote`, { acao, competencia, empresa_ids: empresaIds })
      .then(dados<{ task_id: string; total: number }>),
  /** retificadora de UMA empresa: o CNPJ digitado confirma */
  retificar: (id: number, cnpjConfirmacao: string) =>
    api
      .post(`${BASE}/apuracoes/${id}/retificar`, { cnpj_confirmacao: cnpjConfirmacao })
      .then(dados<{ ok: boolean; situacao: string; erro: string | null }>),
  reabrir: (id: number) => api.post(`${BASE}/apuracoes/${id}/reabrir`).then(dados<IApuracaoSn>),
  statusLote: (taskId: string) => api.get(`${BASE}/lote/${taskId}`).then(dados<IStatusLote>),
  /** ZIP com os PDFs do mês; marca as empresas como baixadas. */
  baixarDocumentos: async (body: {
    competencia: string;
    empresa_ids: number[];
    tipos: TipoDocumento[];
    so_nao_baixados: boolean;
  }): Promise<{ arquivo: Blob; nome: string; empresas: string | null }> => {
    try {
      const r = await api.post(`${BASE}/documentos/zip`, body, { responseType: 'blob' });
      const cd: string = r.headers['content-disposition'] ?? '';
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] ?? 'simples-documentos.zip';
      return { arquivo: r.data, nome, empresas: r.headers['x-empresas'] ?? null };
    } catch (e: any) {
      // erro vem como Blob (responseType): devolve o JSON para mensagemErro ler
      const d = e?.response?.data;
      if (d instanceof Blob) {
        try {
          e.response.data = JSON.parse(await d.text());
        } catch {
          /* mantém o erro original */
        }
      }
      throw e;
    }
  },
  urlArquivo: (id: number) =>
    api.get(`${BASE}/arquivos/${id}/url`).then(dados<{ url: string; nome: string }>),
  parametro: (empresaId: number) =>
    api
      .get(`${BASE}/parametros/${empresaId}`)
      .then(dados<{ is_simples_nacional: boolean; parametro: IParametroSn | null }>),
  salvarParametro: (empresaId: number, body: IParametroSn) =>
    api.put(`${BASE}/parametros/${empresaId}`, body).then(dados<IParametroSn>),
  receitas: (empresaId: number) =>
    api.get(`${BASE}/receitas-brutas/${empresaId}`).then(dados<IValorMensal[]>),
  salvarReceitas: (empresaId: number, body: IValorMensal[]) =>
    api.put(`${BASE}/receitas-brutas/${empresaId}`, body).then(dados),
  folhas: (empresaId: number) => api.get(`${BASE}/folhas/${empresaId}`).then(dados<IValorMensal[]>),
  salvarFolhas: (empresaId: number, body: IValorMensal[]) =>
    api.put(`${BASE}/folhas/${empresaId}`, body).then(dados),
  resumoTom: () => api.get(`${BASE}/municipios-tom/resumo`).then(dados<{ total: number }>),
  importarTom: (arquivo: File) => {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    return api
      .post(`${BASE}/municipios-tom`, fd)
      .then(dados<{ novos: number; atualizados: number; ignorados: number }>);
  },
  faturas: (params: Record<string, string | number | undefined>) =>
    api.get(`${BASE}/faturas`, { params }).then(dados<IListaFaturas>),
  criarFatura: (body: IFatura) => api.post(`${BASE}/faturas`, body).then(dados<IFatura>),
  editarFatura: (id: number, body: IFatura) =>
    api.put(`${BASE}/faturas/${id}`, body).then(dados<IFatura>),
  excluirFatura: (id: number) => api.delete(`${BASE}/faturas/${id}`).then(dados),
};
